'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface RegistrationForm {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

const RegisterPage = () => {
  const router = useRouter();
  const { signUp, user, role, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState<RegistrationForm>({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Already-authenticated visitors shouldn't see the registration form.
  useEffect(() => {
    if (authLoading || !user) return;
    if (role === 'admin') {
      router.replace('/admin');
    } else if (role === 'manager') {
      router.replace('/manager-dashboard');
    } else {
      router.replace('/customer-dashboard');
    }
  }, [authLoading, user, role, router]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.fullName || !formData.email || !formData.phone || !formData.password) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      // Create auth user (phone included in metadata so trigger can set it server-side, avoiding RLS issues when email confirmation is required)
      const authData = await signUp(formData.email, formData.password, {
        fullName: formData.fullName,
        phone: formData.phone,
      });

      if (authData?.user) {
        // Use server-side API route with service role to create profile, bypassing RLS
        const res = await fetch('/api/auth/create-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: authData.user.id,
            email: authData.user.email ?? formData.email,
            fullName: formData.fullName,
            phone: formData.phone || null,
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          console.error('Profile create/update failed:', data.error);
          setError('Account created but profile setup failed. Please try signing in or contact support.');
          setLoading(false);
          return;
        }
        router.push('/customer-portal');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Create Your <span className="text-primary">Account</span>
          </h1>
          <p className="text-muted-foreground">Register to access your customer portal</p>
        </div>

        {/* Registration Form */}
        <div className="container-card p-8">
          {/* Loading State Announcement */}
          {loading && (
            <div className="sr-only" aria-live="assertive" aria-atomic="true">
              Creating your account, please wait...
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" role="alert" aria-live="assertive">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px]"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px]"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px]"
                placeholder="(863) 440-9462"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px]"
                placeholder="••••••••"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 6 characters</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                Confirm Password *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px]"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-8 py-4 sm:py-5 hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg touch-manipulation min-h-[56px]"
            >
              {loading ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account
                  <Icon name="ArrowRightIcon" size={20} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link href="/login" className="text-primary hover:text-primary/80 font-semibold">
                Sign In
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link href="/homepage" className="text-sm text-primary hover:text-primary/80">
              Back to Homepage
            </Link>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-8 container-card p-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <Icon name="InformationCircleIcon" size={24} className="text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-2">What you'll get:</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Access to your customer portal</li>
                <li>• View and track your quotes</li>
                <li>• Manage appointments</li>
                <li>• Direct messaging with our team</li>
                <li>• View invoices and payment instructions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;